import { useEffect, useState } from "react";
import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";

import {
  addMeditation,
  getMeditation,
  getMeditations,
  updateMeditation,
} from "@/api";
import type { Meditation } from "@/types";
import { useDebouncedQuery } from "./useDebouncedQuery";

export const meditationsQueryKeys = {
  useMeditation: (meditationId?: Meditation["id"]) => [
    "meditations",
    "getMeditation",
    meditationId,
  ],
  useManagedPractice: () => ["practices", "getPractices"],
  useMeditations: () => ["useMeditations", "getMeditation"],
  useMeditationsPage: (
    page: number,
    searchQuery: string,
    filters?: Record<string, string>,
  ) => ["useMeditations", "getMeditation", page, searchQuery, filters],
  useInfiniteMeditations: (
    searchQuery: string,
    filters?: Record<string, string>,
  ) => ["useMeditations", "infinite", searchQuery, filters],
};

export const useMeditations = () => {
  const useAllMeditations = (
    page: number,
    searchQuery: string,
    filters?: Record<string, string>,
  ) =>
    useDebouncedQuery({
      queryFn: () => getMeditations(page, searchQuery, filters),
      queryKey: meditationsQueryKeys.useMeditationsPage(
        page,
        searchQuery,
        filters,
      ),
    });
  // Every page of results, loaded on demand (e.g. as a picker is scrolled).
  const useInfiniteMeditations = (
    searchQuery: string,
    filters?: Record<string, string>,
  ) => {
    const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);

    useEffect(() => {
      const timerId = setTimeout(() => setDebouncedSearch(searchQuery), 300);
      return () => clearTimeout(timerId);
    }, [searchQuery]);

    const query = useInfiniteQuery({
      queryKey: meditationsQueryKeys.useInfiniteMeditations(
        debouncedSearch,
        filters,
      ),
      queryFn: ({ pageParam }) =>
        getMeditations(pageParam, debouncedSearch, filters),
      initialPageParam: 1,
      getNextPageParam: (lastPage, _allPages, lastPageParam) =>
        lastPageParam < (lastPage.pagination?.totalPages ?? 0)
          ? lastPageParam + 1
          : undefined,
    });

    return {
      meditations: query.data?.pages.flatMap((page) => page.data) ?? [],
      total: query.data?.pages[0]?.pagination?.total,
      isLoading: query.isLoading || debouncedSearch !== searchQuery,
      hasNextPage: query.hasNextPage,
      isFetchingNextPage: query.isFetchingNextPage,
      fetchNextPage: query.fetchNextPage,
    };
  };
  const useMeditation = (meditationId?: Meditation["id"]) =>
    useQuery({
      queryFn: () => getMeditation(meditationId),
      queryKey: meditationsQueryKeys.useMeditation(meditationId),
      enabled: !!meditationId,
    });
  const useAddMeditation = () => useMutation({ mutationFn: addMeditation });
  const useUpdateMeditation = () =>
    useMutation({ mutationFn: updateMeditation });

  return {
    useAllMeditations,
    useInfiniteMeditations,
    useMeditation,
    useAddMeditation,
    useUpdateMeditation,
  };
};
